import React, { useMemo, useState, useCallback, useEffect } from "react"
import PropTypes from "prop-types"
import moment from "moment"
import { Button } from "antd"
import { useDispatch, useSelector } from "react-redux"
import EventBlock from "./EventBlock"
import { getEventColor, getEventBorderColor } from "pagesV2/pro/Calendar/utils"
import BookingDetailsModal from "pagesV2/pro/clients/ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/Modals/BookingDetails"
import { useRefreshCalendarBookings } from "pagesV2/pro/Calendar/useRefreshCalendarBookings"
import useSearchFilter from "hooks/useSearchFilter"
import { getCalendarEquipment } from "store/calendar"
import styles from "./index.module.css"

const MAX_VISIBLE = 4

const formatEventLabels = (ev) => {
  const startMoment = ev.start ? moment(ev.start, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null
  const endMoment = ev.end ? moment(ev.end, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null
  return {
    ...ev,
    startLabel: startMoment?.isValid() ? startMoment.format("h:mm A") : ev.start,
    endLabel: endMoment?.isValid() ? endMoment.format("h:mm A") : ev.end,
  }
}

const BookingList = ({ events, onBookingClick }) => {
  const [visibleCount, setVisibleCount] = useState(MAX_VISIBLE)
  const visible = events.slice(0, visibleCount)
  const hasMore = visibleCount < events.length
  const canShowLess = visibleCount > MAX_VISIBLE && events.length > MAX_VISIBLE

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {visible.map((ev) => (
        <EventBlock
          key={ev.id ?? (ev.title + ev.start)}
          event={ev}
          eventColor={getEventColor(ev.payment_status)}
          eventBorderColor={getEventBorderColor(ev.payment_status)}
          setIsBookingModalOpen={(open) => { if (open) onBookingClick(ev.id) }}
          setSelectedBookingId={() => {}}
        />
      ))}
      {events.length === 0 && (
        <span style={{ color: "var(--color-text-secondary-dark)", fontSize: "var(--font-size-s)" }}>
          No bookings
        </span>
      )}
      {hasMore && (
        <Button type="link" size="small" onClick={() => setVisibleCount((c) => c + MAX_VISIBLE)}>
          View more
        </Button>
      )}
      {canShowLess && (
        <Button type="link" size="small" onClick={() => setVisibleCount(MAX_VISIBLE)}>
          Read less
        </Button>
      )}
    </div>
  )
}

BookingList.propTypes = {
  events: PropTypes.array.isRequired,
  onBookingClick: PropTypes.func.isRequired,
}

const ListViewGrid = ({ events, activeTab, crewIds, equipmentIds, categoryIds, roleIds, onChangeFilters }) => {
  const dispatch = useDispatch()
  const [params] = useSearchFilter({ accessAttributes: ["date"] })
  const today = moment().format("YYYY-MM-DD")
  const selectedDate = params.date || today

  // On mount, reset URL date to today
  useEffect(() => {
    if (onChangeFilters) {
      onChangeFilters({ target: { name: "date", value: today } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)
  const [selectedBookingId, setSelectedBookingId] = useState(null)
  const [allEquipment, setAllEquipment] = useState([])
  const calendarCrew = useSelector((state) => state.staff?.calendarCrew || [])
  const refreshCalendarBookings = useRefreshCalendarBookings()

  const handleBookingClick = useCallback((id) => {
    setSelectedBookingId(id)
    setIsBookingModalOpen(true)
  }, [])

  const handleBookingCancel = () => { setIsBookingModalOpen(false); setSelectedBookingId(null) }
  const handleBookingCancelSuccess = () => { handleBookingCancel(); refreshCalendarBookings() }

  useEffect(() => {
    if (activeTab !== "equipment") return
    dispatch(getCalendarEquipment())
      .then((res) => setAllEquipment((res.data || []).map((eq) => ({ id: eq.id, name: eq.equipment_title }))))
      .catch(() => {})
  }, [dispatch, activeTab])

  const dayEvents = useMemo(() =>
    events
      .filter((ev) => moment(ev.date).isSame(selectedDate, "day"))
      .map(formatEventLabels),
    [events, selectedDate]
  )

  // Crew tab: group by crew member
  const crewMembers = useMemo(() => {
    if (activeTab !== "crew") return []
    const active = calendarCrew
      .filter((c) => c.status?.toLowerCase() === "active")
      .map((c) => ({ id: c.id, name: c.name }))

    const assignedCrewIds = new Set()
    if (roleIds?.length > 0) {
      events.forEach((ev) => {
        (ev.assigned_crew || []).forEach((c) => {
          if (c.profile_id) assignedCrewIds.add(c.profile_id)
          if (c.id) assignedCrewIds.add(c.id)
        })
      })
    }

    if (crewIds?.length > 0 && roleIds?.length > 0) {
      const merged = new Set([...crewIds.filter((id) => id !== "unassigned"), ...assignedCrewIds])
      return active.filter((c) => merged.has(c.id))
    }
    if (crewIds?.length > 0) return active.filter((c) => crewIds.includes(c.id))
    if (roleIds?.length > 0) return active.filter((c) => assignedCrewIds.has(c.id))
    return active
  }, [activeTab, calendarCrew, crewIds, roleIds, events])

  const showUnassigned = useMemo(() => {
    if (activeTab !== "crew") return false
    return crewIds?.includes("unassigned") || !crewIds?.length
  }, [activeTab, crewIds])

  const unassignedEvents = useMemo(() =>
    dayEvents.filter((ev) => !ev.assigned_crew || ev.assigned_crew.length === 0),
    [dayEvents]
  )

  const eventsByCrewMember = useMemo(() => {
    if (activeTab !== "crew") return {}
    return crewMembers.reduce((acc, crew) => {
      acc[crew.id] = dayEvents.filter((ev) =>
        (ev.assigned_crew || []).some((c) => c.profile_id === crew.id || c.id === crew.id || c.name === crew.name)
      )
      return acc
    }, {})
  }, [activeTab, crewMembers, dayEvents])

  // Equipment tab: group by equipment
  const equipmentList = useMemo(() => {
    if (activeTab !== "equipment") return []
    const assignedIds = new Set()
    if (categoryIds?.length > 0) {
      events.forEach((ev) => {
        (ev.assigned_equipment || []).forEach((item) => {
          if (item.boat_id) assignedIds.add(item.boat_id)
          if (item.id) assignedIds.add(item.id)
        })
      })
    }
    if (equipmentIds?.length > 0 && categoryIds?.length > 0) {
      const merged = new Set([...equipmentIds, ...assignedIds])
      return allEquipment.filter((eq) => merged.has(eq.id))
    }
    if (equipmentIds?.length > 0) return allEquipment.filter((eq) => equipmentIds.includes(eq.id))
    if (categoryIds?.length > 0) return allEquipment.filter((eq) => assignedIds.has(eq.id))
    return allEquipment
  }, [activeTab, allEquipment, equipmentIds, categoryIds, events])

  const eventsByEquipment = useMemo(() => {
    if (activeTab !== "equipment") return {}
    return equipmentList.reduce((acc, eq) => {
      acc[eq.id] = dayEvents.filter((ev) =>
        (ev.assigned_equipment || []).some((item) => item.name === eq.name || item.boat_id === eq.id)
      )
      return acc
    }, {})
  }, [activeTab, equipmentList, dayEvents])

  const renderContent = () => {
    if (activeTab === "crew") {
      const allCrewEvents = [
        ...(showUnassigned ? unassignedEvents : []),
        ...crewMembers.flatMap((crew) => eventsByCrewMember[crew.id] || [])
      ]
      return <BookingList events={allCrewEvents} onBookingClick={handleBookingClick} />
    }

    if (activeTab === "equipment") {
      const allEquipmentEvents = equipmentList.flatMap((eq) => eventsByEquipment[eq.id] || [])
      return <BookingList events={allEquipmentEvents} onBookingClick={handleBookingClick} />
    }

    // all-bookings / my-bookings
    return <BookingList events={dayEvents} onBookingClick={handleBookingClick} />
  }

  return (
    <>
      <div className={styles.nonAgendaWrapper} style={{ height: "calc(100vh - 280px)" }}>
        <div style={{ padding: "12px 16px" }}>
          {renderContent()}
        </div>
      </div>

      {isBookingModalOpen && (
        <BookingDetailsModal
          open={isBookingModalOpen}
          handleOnCancel={handleBookingCancel}
          bookingUuid={selectedBookingId}
          onCancelSuccess={handleBookingCancelSuccess}
        />
      )}
    </>
  )
}

ListViewGrid.propTypes = {
  events: PropTypes.array.isRequired,
  activeTab: PropTypes.string,
  crewIds: PropTypes.array,
  equipmentIds: PropTypes.array,
  categoryIds: PropTypes.array,
  roleIds: PropTypes.array,
  onChangeFilters: PropTypes.func,
}

export default ListViewGrid
